/**
 * The Tools panel's Phone section (web): the hearing-party number, the
 * keypad (DTMF once the line is up), Call and End, extra hearing parties,
 * VideoMail and VCO. It replaced the floating VRS dialer: the keypad is a
 * section of the interpreter's Tools, not its own window. The room owns the
 * calls themselves (Conference.tsx); this section only asks for them.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { hearingPartyAddFailureText } from '../hearingPartyCopy';
import { twilioVoiceService } from '../services/TwilioVoiceService';
import VcoAudioControls from './VcoAudioControls.web';
const DIGITS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];
const useStyles = makeStyles()(theme => {
    return {
        numberRow: {
            display: 'flex',
            gap: 8
        },
        input: {
            background: theme.palette.ui02,
            border: `1px solid ${theme.palette.ui04}`,
            borderRadius: 8,
            boxSizing: 'border-box',
            color: theme.palette.text01,
            flex: 1,
            fontFamily: 'monospace',
            fontSize: 18,
            minHeight: 48,
            minWidth: 0,
            padding: '0 12px',
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.focus01}`,
                outlineOffset: 1
            }
        },
        keypad: {
            display: 'grid',
            gap: 8,
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            marginTop: 8
        },
        key: {
            background: theme.palette.ui02,
            border: `1px solid ${theme.palette.ui04}`,
            borderRadius: 8,
            color: theme.palette.text01,
            cursor: 'pointer',
            fontSize: 20,
            fontWeight: 700,
            minHeight: 44,
            minWidth: 44,
            '&:hover:not(:disabled)': {
                background: theme.palette.ui03
            },
            '&:disabled': {
                cursor: 'default',
                opacity: 0.5
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.focus01}`,
                outlineOffset: 1
            }
        },
        action: {
            border: 0,
            borderRadius: 8,
            color: theme.palette.text01,
            cursor: 'pointer',
            fontSize: 15,
            fontWeight: 700,
            marginTop: 8,
            minHeight: 48,
            width: '100%',
            '&:disabled': {
                cursor: 'default',
                opacity: 0.5
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.focus01}`,
                outlineOffset: 2
            }
        },
        call: { background: theme.palette.action01 },
        end: { background: theme.palette.actionDanger },
        secondary: {
            background: theme.palette.ui02,
            border: `1px solid ${theme.palette.ui04}`
        },
        status: {
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'wrap',
            fontSize: 14,
            gap: 8,
            lineHeight: '20px',
            marginTop: 8
        },
        dot: {
            borderRadius: '50%',
            height: 8,
            width: 8
        },
        connectedDot: { background: '#4ADE80' },
        waitingDot: { background: '#FBBF24' },
        failedDot: { background: '#F87171' },
        timer: {
            fontVariantNumeric: 'tabular-nums'
        },
        error: {
            color: theme.palette.textError,
            fontSize: 14,
            lineHeight: '20px',
            marginTop: 4
        },
        subsection: {
            borderTop: `1px solid ${theme.palette.ui03}`,
            marginTop: 16,
            paddingTop: 12
        },
        small: {
            border: 0,
            borderRadius: 8,
            color: theme.palette.text01,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 700,
            minHeight: 44,
            padding: '0 12px',
            '&:disabled': {
                cursor: 'default',
                opacity: 0.5
            },
            '&:focus-visible': {
                outline: `2px solid ${theme.palette.focus01}`,
                outlineOffset: 2
            }
        },
        line: {
            alignItems: 'center',
            display: 'flex',
            fontSize: 13,
            gap: 8,
            justifyContent: 'space-between',
            lineHeight: '18px',
            marginTop: 6
        },
        vco: {
            marginTop: 12
        }
    };
});
/**
 * The Phone section of the interpreter's Tools.
 *
 * @param {IPhoneSectionProps} props - The room's phone state and handlers.
 * @returns {ReactElement}
 */
export default function PhoneSection({ callDuration, callError, callStatus, canLeaveVoicemail, connectionMessage, hangupPending, initialPhoneNumber, onCall, onHangup, onLeaveVoicemail, onSendDigits, vcoEnabled, vcoRoomName, voicemailTargetName }) {
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const appliedInitialPhoneRef = useRef('');
    const [phoneNumber, setPhoneNumber] = useState(() => initialPhoneNumber?.trim() || '');
    const [endBusy, setEndBusy] = useState(false);
    const [endError, setEndError] = useState();
    const endInFlight = useRef(false);
    // Add a third or fourth hearing party to the live phone conference.
    const [addNumber, setAddNumber] = useState('');
    const [addStatus, setAddStatus] = useState('');
    const [addedCount, setAddedCount] = useState(0);
    const isCallActive = Boolean(hangupPending) || ['connected', 'dialing', 'ringing'].includes(callStatus);
    const canAddHearingParty = !hangupPending && callStatus === 'connected' && twilioVoiceService.canAddParticipant();
    // The client's requested number fills the field once per call, never
    // over what the interpreter typed.
    useEffect(() => {
        const next = initialPhoneNumber?.trim() || '';
        if (next && next !== appliedInitialPhoneRef.current) {
            appliedInitialPhoneRef.current = next;
            setPhoneNumber(current => current || next);
        }
    }, [initialPhoneNumber]);
    const handleEnd = useCallback(async () => {
        if (endInFlight.current) {
            return;
        }
        endInFlight.current = true;
        setEndBusy(true);
        setEndError(undefined);
        try {
            await onHangup();
        }
        catch (error) {
            setEndError(error instanceof Error ? error.message : t('vrs.tools.endNotConfirmed'));
        }
        finally {
            endInFlight.current = false;
            setEndBusy(false);
        }
    }, [onHangup, t]);
    const handleCall = useCallback(() => {
        const target = phoneNumber.trim();
        if (target) {
            onCall(target);
        }
    }, [onCall, phoneNumber]);
    const handleDigitPress = useCallback((digit) => {
        if (isCallActive && onSendDigits) {
            onSendDigits(digit);
            return;
        }
        setPhoneNumber(previous => previous + digit);
    }, [isCallActive, onSendDigits]);
    const handleAddParticipant = useCallback(async () => {
        const target = addNumber.trim();
        if (!target) {
            return;
        }
        setAddStatus(t('vrs.tools.dialing'));
        const outcome = await twilioVoiceService.addParticipantOutcome(target);
        setAddedCount(twilioVoiceService.addedParticipantCount());
        if (outcome.ok) {
            setAddNumber('');
            setAddStatus(t('vrs.tools.added'));
        }
        else {
            // A blocked country or an invalid number says so; anything else is worth another try.
            setAddStatus(hearingPartyAddFailureText(outcome.code, t));
        }
    }, [addNumber, t]);
    const onNumberChange = useCallback((event) => {
        setPhoneNumber(event.target.value);
    }, []);
    const onBackspace = useCallback(() => {
        setPhoneNumber(previous => previous.slice(0, -1));
    }, []);
    const onAddNumberChange = useCallback((event) => {
        setAddNumber(event.target.value);
    }, []);
    // One handler for the twelve keys; each key names its digit.
    const onKey = useCallback((event) => {
        const digit = event.currentTarget.dataset.digit;
        if (digit) {
            handleDigitPress(digit);
        }
    }, [handleDigitPress]);
    const handleDropAdded = useCallback(async () => {
        const dropped = await twilioVoiceService.dropLastAddedParticipant();
        setAddedCount(twilioVoiceService.addedParticipantCount());
        setAddStatus(dropped ? t('vrs.tools.dropped') : t('vrs.tools.dropFailed'));
    }, [t]);
    const statusText = hangupPending
        ? endBusy ? t('vrs.tools.ending') : t('vrs.tools.endUnconfirmed')
        : connectionMessage || (callStatus === 'idle' ? '' : t(`vrs.tools.status.${callStatus}`));
    const dot = callStatus === 'failed' || hangupPending ? classes.failedDot
        : callStatus === 'connected' ? classes.connectedDot
            : callStatus === 'dialing' || callStatus === 'ringing' ? classes.waitingDot : undefined;
    return (React.createElement("div", { "data-phone-section": 'true' },
        React.createElement("div", { className: classes.numberRow },
            React.createElement("input", { "aria-label": t('vrs.tools.phoneNumber'), className: classes.input, disabled: isCallActive, maxLength: 20, onChange: onNumberChange, placeholder: t('vrs.tools.phoneNumber'), type: 'tel', value: phoneNumber }),
            React.createElement("button", { "aria-label": t('vrs.tools.backspace'), className: classes.key, disabled: !phoneNumber || isCallActive, onClick: onBackspace, type: 'button' }, "\u232B")),
        React.createElement("div", { "aria-label": t('vrs.tools.keypad'), className: classes.keypad, role: 'group' }, DIGITS.map(digit => (React.createElement("button", { "aria-label": isCallActive ? `${t('vrs.tools.send')} ${digit}` : digit, className: classes.key, "data-digit": digit, disabled: hangupPending || (isCallActive && callStatus !== 'connected'), key: digit, onClick: onKey, type: 'button' }, digit)))),
        isCallActive
            ? React.createElement("button", { "aria-busy": endBusy, className: cx(classes.action, classes.end), disabled: endBusy, onClick: handleEnd, type: 'button' }, endBusy ? t('vrs.tools.ending') : hangupPending ? t('vrs.tools.retryEnd') : t('vrs.tools.end'))
            : React.createElement("button", { className: cx(classes.action, classes.call), disabled: !phoneNumber.trim(), onClick: handleCall, type: 'button' }, t('vrs.tools.call')),
        statusText && React.createElement("div", { className: classes.status },
            dot && React.createElement("span", { "aria-hidden": 'true', className: cx(classes.dot, dot) }),
            React.createElement("span", { "aria-live": 'polite', role: 'status' }, statusText),
            callStatus === 'connected' && !connectionMessage && !hangupPending && callDuration && (React.createElement("span", { "aria-live": 'off', className: classes.timer, role: 'timer' }, callDuration))),
        (callError || endError) && (React.createElement("div", { className: classes.error, role: 'alert' }, callError || endError)),
        canAddHearingParty && (React.createElement("div", { className: classes.subsection },
            React.createElement("div", { className: classes.numberRow },
                React.createElement("input", { "aria-label": t('vrs.tools.addHearingParty'), className: classes.input, maxLength: 20, onChange: onAddNumberChange, placeholder: t('vrs.tools.addHearingParty'), type: 'tel', value: addNumber }),
                React.createElement("button", { className: cx(classes.small, classes.call), disabled: !addNumber.trim(), onClick: handleAddParticipant, type: 'button' }, t('vrs.tools.add'))),
            (addStatus || addedCount > 0) && (React.createElement("div", { className: classes.line },
                React.createElement("span", { "aria-live": 'polite' }, [addStatus, addedCount > 0 ? t('vrs.tools.addedLines', { count: addedCount }) : '']
                    .filter(Boolean).join(' · ')),
                addedCount > 0 && (React.createElement("button", { className: cx(classes.small, classes.secondary), onClick: handleDropAdded, type: 'button' }, t('vrs.tools.dropLast'))))))),
        onLeaveVoicemail && (React.createElement("button", { className: cx(classes.action, classes.secondary), disabled: !canLeaveVoicemail || hangupPending, onClick: onLeaveVoicemail, title: voicemailTargetName, type: 'button' }, t('vrs.tools.leaveVideoMail'))),
        vcoEnabled && vcoRoomName && (React.createElement("div", { className: classes.vco },
            React.createElement(VcoAudioControls, { role: 'interpreter', roomName: vcoRoomName })))));
}
